import Banner from '@/components/Banner';
import Chapters, { Chapter } from '@/components/Chapters';
import ImageGallery from '@/components/ImageGallery';
import { Reveal, RevealGroup, RevealItem } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import { Badge } from '@/components/ui/badge';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';

const organizations = [
  'Rotary Club of Tiruchi Thamizhachi',
  'Rotary Club of Tiruchirapalli Butterflies',
  'Rotary Club of Smart City Salem',
  'Innerwheel Club of Tiruchirapalli',
  'Innerwheel Club of Tiruchirapalli Malaikottai',
  'All Women Trichy Clubs',
  'Holy Cross College Trichy',
  'Cauvery College of Trichy',
  'PABCET - Pavendar Bharathidasan College of Engineering and Technology',
  'OOR Kaaval Padai of Trichy',
  'Bishop Heber College of Trichy',
  'Dhirajlal Gandhi College of Technology Salem',
  'Shri Shakthi Kailash Women’s College Salem',
  'AVS College of Arts and Science Salem',
];

const stats = [
  { value: '9,000+', label: 'women trained' },
  { value: '2015', label: 'running since' },
  { value: 'Free', label: 'always, of cost' },
];

/* Descriptions are the original copy; taglines and titles are new, so the
   programme's story can be scanned rather than only read straight through. */
const chapters: Chapter[] = [
  {
    tagline: 'What it covers',
    title: 'Free self-defence training',
    description:
      'Dedicated to providing free self-defence training, FETTACK addresses a spectrum of female attacks — from handbag and chain snatching to combating sexual harassment in buses and public spaces. It fosters awareness about good touch and bad touch, while also equipping working women with protective strategies against senior officers. Certificates of training are organised, not just as recognition, but as an encouragement for women to embody the essence of FETTACK.',
  },
  {
    tagline: 'Who it reaches',
    title: 'Schools, colleges and workplaces',
    description:
      'This initiative aims to cultivate boldness and resilience among young girls and ladies in schools, colleges, and workplaces. Since 2015, FETTACK has collaborated with various organisations — schools, colleges and corporates — offering this invaluable training free of cost, conducted by certified Kungfu masters.',
  },
  {
    tagline: '2015 onward',
    title: 'The PABCET collaboration',
    description:
      'A groundbreaking collaboration with PABCET College unfolded, reaching 1,000 beneficiaries, including college students, All Trichy Women Clubs, Innerwheel Clubs, Rotary Clubs, and Oor Kaval Padai lady police teams. This collaboration extended its reach to diverse areas until 2023.',
  },
  {
    tagline: 'A stalwart ally',
    title: 'Pothys Trichy',
    description:
      'A special nod of appreciation goes to Pothys Trichy for joining hands and providing FETTACK training to over 450 lady staff members, with certificates distributed completely free of cost.',
  },
];

const FETTACK = ({ images }: { images: string[] }) => {
  const urls = images.map((image) => `/storage/${image}`);

  const chapterImages = urls.slice(1, chapters.length + 1);
  const galleryImages = urls.slice(chapters.length + 1);

  return (
    <>
      <Banner
        image={urls[0]}
        alt="A FETTACK self-defence training session"
        tagline="Protection against female attacks"
        title="FETTACK - Signature Event"
        description="An exclusive signature project crafted and led by the founder of Samuthrika Academy, Rtn Thamizhachi Priya Govindaraj, since 2015."
      >
        <ShareDialog
          url={window.location.href}
          title="FETTACK - Protection against Female Attacks"
          content="Embark on a journey of empowerment with FETTACK, an exclusive Signature Project meticulously crafted and led by the visionary CEO & Founder of Samuthrika Academy, Rtn Thamizhachi Priya Govindaraj since 2015."
        />
      </Banner>

      {/* The programme's numbers were buried mid-paragraph. They are the
          strongest thing on the page, so they lead it. */}
      <Band tone="indigo" pattern="stitch" patternOpacity="medium">
        <SectionLayout space="default">
          <RevealGroup className="grid gap-8 sm:grid-cols-3">
            {stats.map((stat) => (
              <RevealItem key={stat.label} className="flex flex-col gap-1">
                <span className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
                  {stat.value}
                </span>
                <Typography.Small className="text-indigo-foreground/70">
                  {stat.label}
                </Typography.Small>
              </RevealItem>
            ))}
          </RevealGroup>
        </SectionLayout>
      </Band>

      <Band tone="raised" pattern="lattice">
        <SectionLayout space="lg">
          <Chapters chapters={chapters} images={chapterImages} />
        </SectionLayout>
      </Band>

      <Band tone="raised" seam>
        <SectionLayout space="lg">
          <Reveal className="flex max-w-2xl flex-col gap-3">
            <Typography.Eyebrow>With thanks to</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              The organisations that championed FETTACK
            </Typography.Display2>
          </Reveal>

          <Reveal className="flex flex-wrap gap-2">
            {organizations.map((organization) => (
              <Badge
                key={organization}
                variant="secondary"
                className="py-1 text-sm font-medium"
              >
                {organization}
              </Badge>
            ))}
          </Reveal>

          {galleryImages.length > 0 && (
            <ImageGallery
              images={galleryImages}
              alt="Photographs from FETTACK training sessions"
            />
          )}

          <Reveal>
            <Typography.Body className="max-w-prose text-muted-foreground">
              The impact of FETTACK has resonated through articles in leading
              newspapers and special interviews on Suryam FM. These accolades
              inspire us to push forward, aiming to achieve{' '}
              <span className="font-semibold text-primary">
                our goal of training 10,000 women in the near future
              </span>
              .
            </Typography.Body>
          </Reveal>
        </SectionLayout>
      </Band>
    </>
  );
};

export default FETTACK;
